// src/pages/TaskDefinitionEditorPage.jsx
import React, {
  useState, useCallback, useEffect, useRef, createContext, useContext,
} from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors, closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, horizontalListSortingStrategy,
  useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box, Typography, Stack, IconButton, TextField, Button,
  Switch, FormControlLabel, alpha, CircularProgress, Tooltip,
  Snackbar, Alert, Tabs, Tab, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Divider, Chip,
} from '@mui/material';
import {
  Add, ArrowBack, CheckCircle, TextFields, Numbers,
  ArrowDropDown, Image as ImageIcon, Draw, Notes, DragIndicator,
  Close, Add as AddIcon, Warning, Edit,
} from '@mui/icons-material';
import { selectToken } from '../store/authSlice';

const API_BASE = 'https://monitoring-api.govirtual.cloud/api/v1';
const PALETTE_PREFIX = '__palette__';
const STEP_PREFIX = '__step__';

// ── Phone dimensions ──────────────────────────────────────────────────────────
// Width = height * ratio. The phone fills the container height and derives its width.
const PHONE_W_TO_H = 420 / 800; // width-to-height ratio
const PREVIEW_SCALE = 1.3;
const ps = (rem) => `${parseFloat(rem) * PREVIEW_SCALE}rem`;

// ── Icon picker emoji set ─────────────────────────────────────────────────────
const ICON_OPTIONS = [
  '📋', '📝', '✅', '📊', '🗂️', '📁', '📌', '📍', '🗺', '📷',
  '🗃️', '⚙️', '🔧', '🔨', '🛠', '📐', '📏', '🔍', '🗒️', '📎',
  '🗑️', '🚗', '🚛', '🚜', '🏗', '🏢', '🏠', '🌍', '🌿', '🌱',
  '💧', '♻️', '⚡', '🔋', '💡', '🔑', '🛡', '🏆', '⭐', '🎯',
  '📦', '👷‍♂️', '👷‍♀️', '👨‍💼', '👩', '💼', '📱', '🖥️', '📡', '📻',
  '🔬', '🔭', '💊', '🩺', '🏥', '🚑', '🚒', '🚓', '🚧', '⛽',
  '🛒', '🏪', '🏭', '🔐', '📲', '💳', '💰', '💵', '📈', '📉',
  '🎪', '🎨', '🎭', '🎬', '🎤', '🎵', '🏋', '🤝', '🤲', '🙌',
];

const FIELD_TYPES = [
  { type: 'text', label: 'Text Input', icon: <TextFields />, color: '#91ce0f', desc: 'Short text answer' },
  { type: 'number', label: 'Number', icon: <Numbers />, color: '#0a9e74', desc: 'Numeric value' },
  { type: 'select', label: 'Select', icon: <ArrowDropDown />, color: '#7b1fa2', desc: 'Pill button choices' },
  { type: 'textarea', label: 'Text Area', icon: <Notes />, color: '#4a6a85', desc: 'Multi-line text' },
  { type: 'images', label: 'Photo Upload', icon: <ImageIcon />, color: '#c06000', desc: 'Camera / gallery' },
  { type: 'signature', label: 'Signature', icon: <Draw />, color: '#d32f2f', desc: 'Signature capture' },
];

const typeMeta = (t) => FIELD_TYPES.find((f) => f.type === t) || FIELD_TYPES[0];
const typeColor = (t) => typeMeta(t).color;

function slugify(s = '') {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}
function uid() { return Math.random().toString(36).slice(2, 9); }
function genTaskId() { return `task_${uid()}`; }
function emptyField(type = 'text', label = '') {
  return { _uid: uid(), key: slugify(label), label, type, required: false, placeholder: '', options: [], maxImages: 1 };
}
function emptyStep(n) {
  return { _uid: uid(), key: `step_${uid()}`, label: `Step ${n}`, fields: [] };
}
function getAllKeys(steps, excludeUid = null) {
  return steps.flatMap((s) => s.fields)
    .filter((f) => f._uid !== excludeUid)
    .map((f) => f.key).filter(Boolean);
}
function uniqueKey(label, existingKeys) {
  const base = slugify(label);
  if (!base) return '';
  if (!existingKeys.includes(base)) return base;
  let i = 2; while (existingKeys.includes(`${base}_${i}`)) i++;
  return `${base}_${i}`;
}
function uniqueLabel(baseLabel, existingLabels) {
  if (!existingLabels.includes(baseLabel)) return baseLabel;
  let i = 2; while (existingLabels.includes(`${baseLabel} ${i}`)) i++;
  return `${baseLabel} ${i}`;
}

const EMPTY_DEF = {
  id: '', label: '', icon: '📋', color: '#91ce0f', bg: '#e8e8e8',
  getLocation: true, confirmTitle: 'Submit Collection Report', confirmBody: 'Confirm that all details are accurate before submitting.',
  active: true,
  access: { projects: [], roles: [], users: [] },
  fields: [],
};

const makeInitialSteps = () => [{ _uid: uid(), key: 'step_1', label: 'Step 1', fields: [] }];

const EditorCtx = createContext({});

const THIN_SCROLLBAR = {
  '&::-webkit-scrollbar': { width: 3, height: 3 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.18)', borderRadius: 4 },
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(0,0,0,0.18) transparent',
};

// ─── Icon picker dialog ───────────────────────────────────────────────────────
function IconPickerDialog({ open, current, onSelect, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: '16px', p: 1, width: 340 } }}>
      <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: '0.95rem', color: '#0f2137' }}>Choose an icon</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {ICON_OPTIONS.map((emoji) => (
            <Box key={emoji} onClick={() => { onSelect(emoji); onClose(); }}
              sx={{
                width: 40, height: 40, borderRadius: '10px', fontSize: '1.3rem',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', border: `2px solid ${current === emoji ? '#91ce0f' : 'transparent'}`,
                background: current === emoji ? alpha('#91ce0f', 0.08) : '#f5f5f5',
                transition: 'all 0.12s',
                '&:hover': { background: alpha('#91ce0f', 0.1), border: `2px solid ${alpha('#91ce0f', 0.4)}` },
              }}>
              {emoji}
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ─── Confirm text modal — rendered INSIDE the phone preview ──────────────────
function PhoneConfirmModal({ open, onClose, def, update }) {
  return (
    <Box
      onClick={onClose}
      sx={{
        position: 'absolute', inset: 0, zIndex: 50,
        background: open ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0)',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        borderRadius: '36px',
        overflow: 'hidden',
        pointerEvents: open ? 'auto' : 'none',
        transition: 'background 0.25s',
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          background: '#fff',
          borderRadius: '24px 24px 0 0',
          pb: 2,
          transform: open ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s cubic-bezier(0.32,0.72,0,1)',
        }}
      >
        <Box sx={{ pt: 1.5, pb: 0.5, display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: 40, height: 4, borderRadius: 2, background: '#e0e0e0' }} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 1.5, pb: 1, gap: 1 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '16px', background: alpha(def.color || '#91ce0f', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: ps('1.4rem') }}>{def.icon || '📋'}</Typography>
          </Box>
          <TextField
            variant="standard"
            value={def.confirmTitle || ''}
            onChange={(e) => update({ confirmTitle: e.target.value })}
            placeholder="Submit Collection Report"
            inputProps={{ style: { fontSize: ps('0.85rem'), fontWeight: 700, color: '#0f2137', textAlign: 'center' } }}
            sx={{ width: '90%', '& .MuiInput-underline:before': { borderColor: 'transparent' }, '& .MuiInput-underline:hover:before': { borderColor: alpha(def.color || '#91ce0f', 0.3) } }}
          />
          <TextField
            variant="standard"
            value={def.confirmBody || ''}
            onChange={(e) => update({ confirmBody: e.target.value })}
            placeholder="Confirm that all details are accurate before submitting."
            multiline
            inputProps={{ style: { fontSize: ps('0.75rem'), color: '#6b7280', textAlign: 'center' } }}
            sx={{ width: '88%', '& .MuiInput-underline:before': { borderColor: 'transparent' }, '& .MuiInput-underline:hover:before': { borderColor: alpha(def.color || '#91ce0f', 0.3) } }}
          />
        </Box>
        <Box sx={{ px: 2, pt: 0.5 }}>
          <Box onClick={onClose}
            sx={{
              height: 44, borderRadius: '12px', background: def.color || '#91ce0f',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              boxShadow: `0 4px 16px ${alpha(def.color || '#91ce0f', 0.35)}`,
            }}>
            <Typography sx={{ fontSize: ps('0.78rem'), fontWeight: 700, color: '#fff' }}>Submit →</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Palette card ─────────────────────────────────────────────────────────────
function PaletteCard({ ft, isDragging }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      px: 1.5, py: 1.25, borderRadius: '12px',
      border: `1.5px solid ${alpha(ft.color, 0.22)}`,
      background: alpha(ft.color, isDragging ? 0.15 : 0.05),
      cursor: 'grab', userSelect: 'none', opacity: isDragging ? 0.5 : 1, transition: 'all 0.15s',
      '&:hover': { background: alpha(ft.color, 0.11), borderColor: alpha(ft.color, 0.45) },
    }}>
      <Box sx={{ width: 36, height: 36, borderRadius: '9px', background: alpha(ft.color, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {React.cloneElement(ft.icon, { sx: { fontSize: 18, color: ft.color } })}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f2137', lineHeight: 1.2 }}>{ft.label}</Typography>
        <Typography sx={{ fontSize: '0.7rem', color: '#9aacb8' }}>{ft.desc}</Typography>
      </Box>
      <DragIndicator sx={{ fontSize: 16, color: '#c8d4dc' }} />
    </Box>
  );
}

function DraggablePaletteCard({ ft }) {
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: `${PALETTE_PREFIX}${ft.type}`,
    data: { type: 'palette', fieldType: ft.type },
  });
  return (
    <Box ref={setNodeRef} {...attributes} {...listeners} sx={{ touchAction: 'none' }}>
      <PaletteCard ft={ft} isDragging={isDragging} />
    </Box>
  );
}

// ─── Field settings panel ─────────────────────────────────────────────────────
function FieldSettingsPanel({ field, onChange, allKeys }) {
  const [optInput, setOptInput] = useState('');
  const meta = typeMeta(field.type);
  const update = (patch) => onChange({ ...field, ...patch });
  const handleLabelChange = (e) => {
    const label = e.target.value;
    update({ label, key: uniqueKey(label, allKeys) });
  };
  const addOpt = () => {
    const v = optInput.trim(); if (!v) return;
    update({ options: [...(field.options || []), v] });
    setOptInput('');
  };
  const removeOpt = (i) => {
    const opts = [...(field.options || [])]; opts.splice(i, 1); update({ options: opts });
  };
  const hasPlaceholder = ['text', 'number', 'textarea'].includes(field.type);
  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: '10px', background: alpha(meta.color, 0.07), border: `1px solid ${alpha(meta.color, 0.2)}` }}>
        {React.cloneElement(meta.icon, { sx: { fontSize: 16, color: meta.color } })}
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: meta.color }}>{meta.label}</Typography>
      </Box>
      <Stack spacing={1.5}>
        <TextField size="small" label="Label" value={field.label || ''} onChange={handleLabelChange}
          helperText={field.key ? `key: ${field.key}` : 'Key auto-generated'}
          FormHelperTextProps={{ sx: { fontFamily: 'monospace', fontSize: '0.68rem', color: '#9aacb8' } }} />
        {hasPlaceholder && (
          <TextField size="small" label="Placeholder" value={field.placeholder || ''}
            onChange={(e) => update({ placeholder: e.target.value })} />
        )}
        <FormControlLabel
          control={<Switch checked={!!field.required} onChange={(e) => update({ required: e.target.checked })} />}
          label={<Typography sx={{ fontSize: '0.85rem' }}>Required field</Typography>} />
        {field.type === 'images' && (
          <TextField size="small" label="Max photos" type="number" value={field.maxImages || 1}
            onChange={(e) => update({ maxImages: Math.max(1, +e.target.value) })}
            inputProps={{ min: 1, max: 20 }} />
        )}
        {field.type === 'select' && (
          <Box>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#4a6a85', mb: 1, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Options</Typography>
            <Stack spacing={0.75} sx={{ mb: 1.25 }}>
              {(field.options || []).map((opt, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.6, borderRadius: '8px', background: alpha(meta.color, 0.07), border: `1px solid ${alpha(meta.color, 0.18)}` }}>
                  <Typography sx={{ flex: 1, fontSize: '0.82rem', fontWeight: 600, color: meta.color }}>{opt}</Typography>
                  <IconButton size="small" onClick={() => removeOpt(i)} sx={{ p: 0.3, '&:hover': { color: '#d32f2f' } }}>
                    <Close sx={{ fontSize: 13 }} />
                  </IconButton>
                </Box>
              ))}
              {!(field.options || []).length && (
                <Typography sx={{ fontSize: '0.75rem', color: '#bbb', fontStyle: 'italic' }}>No options yet</Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField size="small" placeholder="Add option…" value={optInput}
                onChange={(e) => setOptInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addOpt()} sx={{ flex: 1 }} />
              <IconButton size="small" onClick={addOpt}
                sx={{ background: alpha(meta.color, 0.1), border: `1px solid ${alpha(meta.color, 0.25)}`, borderRadius: '8px', color: meta.color }}>
                <AddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

// ─── Right panel — slides in when a field is selected ────────────────────────
function RightPanel({ selectedField, onClearSelection, onChange, allKeys }) {
  const isOpen = !!selectedField;
  return (
    <Box sx={{
      width: isOpen ? 260 : 0,
      flexShrink: 0,
      overflow: 'hidden',
      transition: 'width 0.28s cubic-bezier(0.4,0,0.2,1), opacity 0.22s',
      opacity: isOpen ? 1 : 0,
    }}>
      <Box sx={{
        width: 260,
        height: '100%',
        background: '#fff',
        borderRadius: '16px',
        border: `1px solid ${selectedField ? alpha(typeColor(selectedField.type), 0.25) : 'rgba(16,117,186,0.1)'}`,
        boxShadow: '0 2px 8px rgba(15,33,55,0.04)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transform: isOpen ? 'translateX(0)' : 'translateX(40px)',
        transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)',
      }}>
        {selectedField && (
          <>
            <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid rgba(16,117,186,0.07)', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#4a6a85', flex: 1 }}>Field Settings</Typography>
              <IconButton size="small" onClick={onClearSelection} sx={{ p: 0.4 }}>
                <Close sx={{ fontSize: 14, color: '#9aacb8' }} />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <FieldSettingsPanel field={selectedField} onChange={onChange} allKeys={allKeys} />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

// ─── Task settings panel ──────────────────────────────────────────────────────
function TaskSettingsPanel({ def, update, onOpenConfirmModal }) {
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [hexInput, setHexInput] = useState(def.color || '#91ce0f');

  useEffect(() => { setHexInput(def.color || '#91ce0f'); }, [def.color]);

  const handleHexChange = (e) => {
    const val = e.target.value;
    setHexInput(val);
    if (/^#[0-9a-fA-F]{6}$/.test(val)) update({ color: val });
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flex: 1 }}>
      <Stack spacing={1.5}>
        <Box>
          <TextField
            size="small"
            label="Task Name"
            value={def.label || ''}
            onChange={(e) => update({ label: e.target.value })}
            placeholder="e.g. Collection Report"
            fullWidth
          />
          <Divider sx={{ mb: 2, mt: 2, borderColor: 'rgba(16,117,186,0.08)' }} />
          <Typography sx={{ fontSize: '0.7rem', color: '#4a6a85', mb: 0.75, fontWeight: 600 }}>Task Icon</Typography>
          <Tooltip title="Click to change icon" placement="top">
            <Box onClick={() => setIconPickerOpen(true)}
              sx={{
                width: 56, height: 56, borderRadius: '14px', background: alpha(def.color || '#91ce0f', 0.08),
                border: `2px solid ${alpha(def.color || '#91ce0f', 0.2)}`, display: 'flex', alignItems: 'center',
                justifyContent: 'center', cursor: 'pointer', fontSize: '1.6rem',
                transition: 'all 0.15s', '&:hover': { border: `2px solid ${def.color || '#91ce0f'}`, background: alpha(def.color || '#91ce0f', 0.14) },
              }}>
              {def.icon || '📋'}
            </Box>
          </Tooltip>
        </Box>

        <Box>
          <Typography sx={{ fontSize: '0.7rem', color: '#4a6a85', mb: 0.75, fontWeight: 600 }}>Preview Color Theme</Typography>
          <Typography sx={{ fontSize: '0.65rem', color: '#9aacb8', mb: 0.75 }}>Only affects the phone preview accent</Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box component="input" type="color" value={def.color || '#91ce0f'} onChange={(e) => { update({ color: e.target.value }); setHexInput(e.target.value); }}
              sx={{ width: 38, height: 34, border: 'none', borderRadius: '8px', cursor: 'pointer', p: 0, flexShrink: 0 }} />
            <TextField
              size="small"
              value={hexInput}
              onChange={handleHexChange}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.75rem', width: 80 }, maxLength: 7, spellCheck: false }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' }, '& .MuiOutlinedInput-input': { py: 0.6, px: 1 } }}
            />
          </Stack>
        </Box>

        <Divider sx={{ borderColor: 'rgba(16,117,186,0.08)' }} />
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#91ce0f', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Confirm Text</Typography>
        <Stack spacing={1}>
          <Box>
            <Typography sx={{ fontSize: '0.62rem', color: '#9aacb8', mb: 0.4, fontWeight: 600 }}>Title</Typography>
            <TextField
              size="small" fullWidth
              value={def.confirmTitle || ''}
              onChange={(e) => update({ confirmTitle: e.target.value })}
              onFocus={onOpenConfirmModal}
              placeholder="e.g. Submit Collection Report"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.62rem', color: '#9aacb8', mb: 0.4, fontWeight: 600 }}>Body</Typography>
            <TextField
              size="small" fullWidth multiline rows={2}
              value={def.confirmBody || ''}
              onChange={(e) => update({ confirmBody: e.target.value })}
              onFocus={onOpenConfirmModal}
              placeholder="e.g. Confirm that all details are accurate…"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
          </Box>
        </Stack>
      </Stack>
      <IconPickerDialog open={iconPickerOpen} current={def.icon} onSelect={(e) => update({ icon: e })} onClose={() => setIconPickerOpen(false)} />
    </Box>
  );
}

// ─── InlineLabelRow ───────────────────────────────────────────────────────────
function InlineLabelRow({ field, isSelected, ringColor, allKeys, onChange }) {
  const handleChange = (e) => {
    const label = e.target.value;
    onChange({ ...field, label, key: uniqueKey(label, allKeys) });
  };
  return (
    <Stack direction="row" spacing={0.4} alignItems="center" sx={{ mb: 0.7 }}>
      {isSelected
        ? <TextField variant="standard" value={field.label} onChange={handleChange}
          onClick={(e) => e.stopPropagation()} placeholder="Field label…" autoComplete="off"
          sx={{ flex: 1, '& .MuiInput-input': { fontSize: ps('0.78rem'), fontWeight: 600, color: '#1a1a1a', py: 0 }, '& .MuiInput-underline:before': { borderColor: 'transparent' }, '& .MuiInput-underline:hover:before': { borderColor: alpha(ringColor, 0.4) } }} />
        : <Typography sx={{ fontSize: ps('0.78rem'), fontWeight: 600, color: field.label ? '#1a1a1a' : '#bbb', flex: 1 }}>{field.label || 'Field label…'}</Typography>
      }
      {field.required && <Typography sx={{ fontSize: ps('0.72rem'), color: '#ef4444' }}>*</Typography>}
    </Stack>
  );
}

// ─── Sortable field card ───────────────────────────────────────────────────────
function SortableFieldCard({ field, stepIdx, isSelected, onSelect, onChange, onDelete, allKeys, isDragOverlay, themeColor }) {
  const [pillSelected, setPillSelected] = useState(null);
  const ringColor = typeColor(field.type);
  const color = themeColor || '#91ce0f';
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field._uid,
    data: { type: 'field', stepIdx, fieldUid: field._uid },
    disabled: isDragOverlay,
  });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0 : 1 };
  const handlePlaceholderChange = (e) => onChange({ ...field, placeholder: e.target.value });
  return (
    <Box ref={setNodeRef} style={style}
      onClick={(e) => { e.stopPropagation(); onSelect(field._uid); }}
      sx={{ position: 'relative', cursor: 'pointer', overflow: 'visible' }}>
      {isSelected && !isDragOverlay && (
        <Box {...attributes} {...listeners} onClick={(e) => e.stopPropagation()}
          sx={{ position: 'absolute', top: -25, left: '50%', transform: 'translateX(-50%)', height: 20, display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, borderRadius: '6px 6px 0 0', background: ringColor, boxShadow: `0 -2px 6px ${alpha(ringColor, 0.25)}`, cursor: 'grab', userSelect: 'none', zIndex: 20, whiteSpace: 'nowrap', touchAction: 'none' }}>
          <DragIndicator sx={{ fontSize: 11, color: '#fff' }} />
          <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.08em' }}>drag</Typography>
        </Box>
      )}
      {isSelected && !isDragOverlay && (
        <Box sx={{ position: 'absolute', inset: `-5px -7px ${field.type === 'signature' || field.type === 'images' ? '0px' : '5px'} -7px`, borderRadius: '10px', border: `2px solid ${ringColor}`, boxShadow: `0 0 0 3px ${alpha(ringColor, 0.1)}`, pointerEvents: 'none', zIndex: 2 }} />
      )}
      {isSelected && !isDragOverlay && (
        <Box onClick={(e) => { e.stopPropagation(); onDelete(); }}
          sx={{ position: 'absolute', top: 2, right: 0, zIndex: 22, width: 20, height: 20, borderRadius: '50%', background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 6px rgba(239,68,68,0.35)' }}>
          <Close sx={{ fontSize: 11, color: '#fff' }} />
        </Box>
      )}
      {field.type === 'select' && (
        <Box sx={{ pb: 0.75 }}>
          <InlineLabelRow field={field} isSelected={isSelected && !isDragOverlay} ringColor={ringColor} allKeys={allKeys} onChange={onChange} />
          <Box sx={{ border: `1.5px solid ${isSelected && !isDragOverlay ? alpha(ringColor, 0.5) : '#e5e7eb'}`, borderRadius: '10px', p: 1, display: 'flex', flexWrap: 'wrap', gap: 0.6, background: '#fafafa', transition: 'border 0.15s' }}>
            {(field.options?.length ? field.options : ['Option 1', 'Option 2']).map((opt, i) => (
              <Box key={i} onClick={(e) => { e.stopPropagation(); setPillSelected(pillSelected === opt ? null : opt); }}
                sx={{ px: 1.5, py: 0.5, borderRadius: '20px', border: `1.5px solid ${pillSelected === opt ? color : '#e5e7eb'}`, background: pillSelected === opt ? color : '#fff', cursor: 'pointer', transition: 'all 0.12s' }}>
                <Typography sx={{ fontSize: ps('0.72rem'), fontWeight: 600, color: pillSelected === opt ? '#fff' : '#555' }}>{opt}</Typography>
              </Box>
            ))}
          </Box>
          <ThinRule />
        </Box>
      )}
      {(field.type === 'text' || field.type === 'number' || field.type === 'textarea') && (
        <Box sx={{ pb: 0.75 }}>
          <InlineLabelRow field={field} isSelected={isSelected && !isDragOverlay} ringColor={ringColor} allKeys={allKeys} onChange={onChange} />
          <Box sx={{ border: `1.5px solid ${isSelected && !isDragOverlay ? alpha(ringColor, 0.5) : '#e5e7eb'}`, borderRadius: '10px', px: 1.25, py: 0.75, background: '#fafafa', minHeight: field.type === 'textarea' ? 52 : 'auto', transition: 'border 0.15s' }}>
            {isSelected && !isDragOverlay
              ? <TextField variant="standard" value={field.placeholder || ''} onChange={handlePlaceholderChange} onClick={(e) => e.stopPropagation()} placeholder={field.type === 'number' ? '0.00' : 'Placeholder text…'} fullWidth multiline={field.type === 'textarea'}
                sx={{ '& .MuiInput-input': { fontSize: ps('0.75rem'), color: '#bbb', py: 0 }, '& .MuiInput-underline:before': { borderColor: 'transparent' }, '& .MuiInput-underline:after': { borderColor: 'transparent' } }} />
              : <Typography sx={{ fontSize: ps('0.75rem'), color: '#bbb' }}>{field.placeholder || (field.type === 'number' ? '0.00' : 'Enter text…')}</Typography>
            }
          </Box>
          <ThinRule />
        </Box>
      )}
      {field.type === 'images' && (
        <Box sx={{ pb: 0.75 }}>
          <InlineLabelRow field={field} isSelected={isSelected && !isDragOverlay} ringColor={ringColor} allKeys={allKeys} onChange={onChange} />
          <Box sx={{ border: `2px dashed ${isSelected && !isDragOverlay ? ringColor : '#93c5fd'}`, borderRadius: '12px', p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.6, background: 'rgba(219,234,254,0.1)', transition: 'border 0.15s' }}>
            <Box sx={{ width: 38, height: 38, borderRadius: '10px', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography>📷</Typography></Box>
            <Typography sx={{ fontSize: ps('0.72rem'), fontWeight: 700, color: color }}>Tap to add photo</Typography>
            <Typography sx={{ fontSize: ps('0.62rem'), color: '#aaa' }}>Up to {field.maxImages || 1} photo{(field.maxImages || 1) > 1 ? 's' : ''}</Typography>
          </Box>
        </Box>
      )}
      {field.type === 'signature' && (
        <Box sx={{ pb: 0.75 }}>
          <InlineLabelRow field={field} isSelected={isSelected && !isDragOverlay} ringColor={ringColor} allKeys={allKeys} onChange={onChange} />
          <Box sx={{ border: `2px dashed ${isSelected && !isDragOverlay ? ringColor : '#93c5fd'}`, borderRadius: '12px', p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.6, background: 'rgba(219,234,254,0.1)', transition: 'border 0.15s' }}>
            <Box sx={{ width: 38, height: 38, borderRadius: '10px', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography>✍️</Typography></Box>
            <Typography sx={{ fontSize: ps('0.72rem'), fontWeight: 700, color: color }}>Tap to sign</Typography>
            <Typography sx={{ fontSize: ps('0.62rem'), color: '#aaa' }}>One signature allowed</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function ThinRule() {
  return <Box sx={{ height: '1px', background: '#f0f0f0', mt: 1 }} />;
}

function StepDropZone({ stepIdx, isOver, color }) {
  const { setNodeRef } = useSortable({ id: `dropzone__${stepIdx}`, data: { type: 'dropzone', stepIdx } });
  return (
    <Box ref={setNodeRef} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 4, gap: 1 }}>
      <Box sx={{ width: 44, height: 44, borderRadius: '50%', border: `2px dashed ${isOver ? color : '#d1d5db'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
        <Add sx={{ fontSize: 20, color: isOver ? color : '#d1d5db' }} />
      </Box>
      <Typography sx={{ fontSize: ps('0.7rem'), color: isOver ? color : '#9ca3af', fontWeight: isOver ? 700 : 400, transition: 'all 0.15s' }}>
        {isOver ? 'Drop to add' : 'Drag fields here'}
      </Typography>
    </Box>
  );
}

// ─── Sortable step tab ────────────────────────────────────────────────────────
function SortableStepTab({ step, si, activeStep, setActiveStep, updateStep, onDeleteRequest, steps, color }) {
  const isActive = si === activeStep;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `${STEP_PREFIX}${step._uid}`,
    data: { type: 'step', stepIdx: si },
  });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1, flexShrink: 0 };
  return (
    <Box ref={setNodeRef} style={style}>
      <Box onClick={() => setActiveStep(si)}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.35, borderRadius: '20px', cursor: 'pointer', background: isActive ? color : 'transparent', border: `1.5px solid ${isActive ? color : '#e5e7eb'}`, transition: 'all 0.15s' }}>
        {isActive && (
          <Box {...attributes} {...listeners} onClick={(e) => e.stopPropagation()}
            sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', touchAction: 'none', mr: -0.25 }}>
            <DragIndicator sx={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }} />
          </Box>
        )}
        <Box sx={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, background: si < activeStep ? '#22c55e' : isActive ? '#fff' : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {si < activeStep
            ? <Typography sx={{ fontSize: '0.48rem', color: '#fff' }}>✓</Typography>
            : <Typography sx={{ fontSize: '.7rem', fontWeight: 700, color: isActive ? color : '#9ca3af' }}>{si + 1}</Typography>
          }
        </Box>
        <TextField variant="standard" value={step.label || ''}
          onClick={(e) => { e.stopPropagation(); setActiveStep(si); }}
          onChange={(e) => updateStep(si, { ...step, label: e.target.value })}
          inputProps={{ style: { fontSize: '.8rem', fontWeight: 700, color: isActive ? '#fff' : '#555', padding: 0, width: `${Math.max(38, (step.label || '').length * 6.5)}px` } }}
          sx={{ '& .MuiInput-underline:before': { borderColor: 'transparent' }, '& .MuiInput-underline:hover:before': { borderColor: 'rgba(255,255,255,0.4)' }, '& .MuiInput-underline:after': { borderColor: 'transparent' } }} />
        {steps.length > 1 && (
          <Box onClick={(e) => { e.stopPropagation(); onDeleteRequest(si); }}
            sx={{ width: 11, height: 11, borderRadius: '50%', background: isActive ? 'rgba(255,255,255,0.28)' : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, '&:hover': { background: isActive ? 'rgba(255,255,255,0.55)' : '#fca5a5' } }}>
            <Close sx={{ fontSize: 7, color: isActive ? '#fff' : '#9ca3af' }} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ─── Phone preview ────────────────────────────────────────────────────────────
function PhonePreview({
  def, steps, activeStep, setActiveStep, addStep, onDeleteStepRequest, updateStep,
  selectedField, setSelectedField, updateField, deleteField, allKeys, overId,
  confirmModalOpen, onCloseConfirmModal, onUpdateDef, onFieldDragWidth,
}) {
  const color = def.color || '#91ce0f';
  const step = steps[activeStep] || { fields: [] };
  const fieldIds = step.fields.map((f) => f._uid);
  const stepIds = steps.map((s) => `${STEP_PREFIX}${s._uid}`);
  const fieldsAreaRef = useRef(null);

  useEffect(() => {
    if (!fieldsAreaRef.current || !onFieldDragWidth) return;
    const ro = new ResizeObserver(() => {
      onFieldDragWidth(fieldsAreaRef.current?.offsetWidth ?? 240);
    });
    ro.observe(fieldsAreaRef.current);
    onFieldDragWidth(fieldsAreaRef.current.offsetWidth);
    return () => ro.disconnect();
  }, [onFieldDragWidth]);

  return (
    <Box sx={{
      height: '100%',
      aspectRatio: `${PHONE_W_TO_H}`,
      maxHeight: '100%',
      mx: 'auto',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
    }}>
      <Box sx={{
        flex: 1, borderRadius: '36px', background: '#fff',
        boxShadow: '0 0 0 8px #1c1c1e, 0 0 0 10px #3a3a3c, 0 24px 64px rgba(0,0,0,0.38)',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
        position: 'relative',
      }}>
        {/* Status bar */}
        <Box sx={{ height: 28, background: '#fff', display: 'flex', alignItems: 'center', px: 2.5, justifyContent: 'space-between', flexShrink: 0 }}>
          <Typography sx={{ fontSize: ps('0.62rem'), fontWeight: 700, color: '#0f2137' }}>9:41</Typography>
          <Box sx={{ width: 76, height: 14, borderRadius: '7px', background: '#1c1c1e' }} />
          <Typography sx={{ fontSize: ps('0.58rem'), color: '#0f2137' }}>●●●</Typography>
        </Box>
        {/* Task name header */}
        <Box sx={{ px: 2, py: 0.9, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Box sx={{ width: 28, height: 28, borderRadius: '7px', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Typography sx={{ fontSize: ps('0.6rem') }}>←</Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <TextField variant="standard" value={def.label || ''} onChange={(e) => def.onLabelChange(e.target.value)} placeholder="Task Name"
              inputProps={{ style: { fontSize: ps('0.85rem'), fontWeight: 700, color: '#0f2137', padding: 0 } }}
              sx={{ width: '100%', '& .MuiInput-underline:before': { borderColor: 'transparent' }, '& .MuiInput-underline:hover:before': { borderColor: alpha(color, 0.35) } }} />
            <Typography sx={{ fontSize: ps('0.6rem'), color: '#888' }}>Step {activeStep + 1} of {steps.length} · {step.label || '…'}</Typography>
          </Box>
          <Box sx={{ width: 28, height: 28, borderRadius: '7px', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Typography sx={{ fontSize: def.icon ? ps('0.85rem') : ps('0.5rem') }}>{def.icon || '📋'}</Typography>
          </Box>
        </Box>
        {/* Step tabs */}
        <Box sx={{ px: 1.5, pt: 1.1, pb: 0.5, flexShrink: 0 }}>
          <SortableContext items={stepIds} strategy={horizontalListSortingStrategy}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ overflowX: 'auto', pb: 0.5, ...THIN_SCROLLBAR }}>
              {steps.map((s, si) => (
                <SortableStepTab key={s._uid} step={s} si={si} activeStep={activeStep} setActiveStep={setActiveStep} updateStep={updateStep} onDeleteRequest={onDeleteStepRequest} steps={steps} color={color} />
              ))}
              <Box onClick={addStep} sx={{ width: 21, height: 21, borderRadius: '50%', flexShrink: 0, border: '1.5px dashed #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s', '&:hover': { border: `1.5px solid ${color}`, background: alpha(color, 0.08) } }}>
                <Add sx={{ fontSize: 11, color: '#9ca3af' }} />
              </Box>
            </Stack>
          </SortableContext>
          <Divider sx={{ mt: 0.75, borderColor: 'rgba(0,0,0,0.07)' }} />
        </Box>
        {/* Fields area */}
        <Box ref={fieldsAreaRef} onClick={() => setSelectedField(null)} sx={{ flex: 1, overflowY: 'auto', px: 1.75, pt: 3, pb: 0.75, ...THIN_SCROLLBAR }}>
          <SortableContext items={step.fields.length ? fieldIds : [`dropzone__${activeStep}`]} strategy={verticalListSortingStrategy}>
            {step.fields.length === 0
              ? <StepDropZone stepIdx={activeStep} isOver={overId === `dropzone__${activeStep}`} color={color} />
              : step.fields.map((field, fi) => (
                <Box key={field._uid} sx={{ position: 'relative' }}>
                  <SortableFieldCard field={field} stepIdx={activeStep} isSelected={selectedField === field._uid} onSelect={setSelectedField} onChange={(updated) => updateField(activeStep, fi, updated)} onDelete={() => deleteField(activeStep, fi)} allKeys={getAllKeys(steps, field._uid)} themeColor={color} />
                </Box>
              ))
            }
          </SortableContext>
        </Box>
        {/* Bottom nav */}
        <Box sx={{ px: 1.75, pb: 1.75, pt: 0.75, flexShrink: 0, borderTop: '1px solid #f5f5f5' }}>
          {steps.length === 1
            ? <NavBtn center="Submit" onCenter={() => onCloseConfirmModal('open')} color={color} />
            : activeStep === 0
              ? <NavBtn center={`Next: ${steps[1]?.label || 'Next'} →`} onCenter={() => setActiveStep(1)} color={color} />
              : activeStep === steps.length - 1
                ? <NavBtn left="← Back" onLeft={() => setActiveStep((s) => s - 1)} center="Submit" onCenter={() => onCloseConfirmModal('open')} color={color} />
                : <NavBtn left="← Back" onLeft={() => setActiveStep((s) => s - 1)} center={`Next: ${steps[activeStep + 1]?.label || 'Next'} →`} onCenter={() => setActiveStep((s) => s + 1)} color={color} />
          }
        </Box>
        {/* Confirm modal inside the phone */}
        <PhoneConfirmModal
          open={confirmModalOpen}
          onClose={onCloseConfirmModal}
          def={def}
          update={onUpdateDef}
        />
      </Box>
    </Box>
  );
}

function NavBtn({ left, center, onLeft, onCenter, color }) {
  const c = color || '#91ce0f';
  return (
    <Stack direction="row" spacing={1}>
      {left && (
        <Box onClick={onLeft} sx={{ height: 44, flex: 0.32, border: '1.5px solid #e5e7eb', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <Typography sx={{ fontSize: ps('0.7rem'), fontWeight: 600, color: '#555' }}>{left}</Typography>
        </Box>
      )}
      <Box onClick={onCenter} sx={{ height: 44, flex: 1, background: c, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: onCenter ? 'pointer' : 'default' }}>
        <Typography sx={{ fontSize: ps('0.78rem'), fontWeight: 700, color: '#fff' }}>{center}</Typography>
      </Box>
    </Stack>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROOT
// ─────────────────────────────────────────────────────────────────────────────
export default function TaskDefinitionEditorPage() {
  const token = useSelector(selectToken);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [def, setDef] = useState(() => ({ ...EMPTY_DEF, id: genTaskId() }));
  const [steps, setSteps] = useState(makeInitialSteps);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedUid, setSelectedUid] = useState(null);
  const [leftTab, setLeftTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [deleteStepDialog, setDeleteStepDialog] = useState({ open: false, stepIdx: null });
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [activeDragId, setActiveDragId] = useState(null);
  const [activeDragData, setActiveDragData] = useState(null);
  const [overId, setOverId] = useState(null);
  const [fieldDragWidth, setFieldDragWidth] = useState(240);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const FIXED_ACCENT = '#91ce0f';

  // ── Load for edit ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    fetch(`${API_BASE}/admin/taskdefinitions/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data) => {
        const d = data.taskDefinition || data;
        const tagF = (fs) => (fs || []).map((f) => ({ ...f, _uid: uid() }));
        const s = (d.steps?.length ? d.steps : [{ key: 'step_1', label: 'Step 1', fields: d.fields || [] }])
          .map((s) => ({ ...s, _uid: uid(), fields: tagF(s.fields) }));
        setDef({ ...EMPTY_DEF, ...d });
        setSteps(s);
      })
      .catch(() => setSnack({ open: true, msg: 'Failed to load', severity: 'error' }))
      .finally(() => setLoading(false));
  }, [id, token, isEdit]);

  const allKeys = getAllKeys(steps);
  const selectedField = selectedUid ? steps.flatMap((s) => s.fields).find((f) => f._uid === selectedUid) || null : null;

  // ── Field / step mutations ─────────────────────────────────────────────────
  const updateField = useCallback((si, fi, updated) => {
    setSteps((prev) => { const n = prev.map((s) => ({ ...s, fields: [...s.fields] })); n[si].fields[fi] = updated; return n; });
  }, []);
  const handleSelectedFieldChange = useCallback((updated) => {
    setSteps((prev) => {
      const si = prev.findIndex((s) => s.fields.some((f) => f._uid === updated._uid));
      if (si < 0) return prev;
      const fi = prev[si].fields.findIndex((f) => f._uid === updated._uid);
      const n = prev.map((s) => ({ ...s, fields: [...s.fields] }));
      n[si].fields[fi] = updated;
      return n;
    });
  }, []);
  const deleteField = useCallback((si, fi) => {
    setSteps((prev) => { const n = prev.map((s) => ({ ...s, fields: [...s.fields] })); n[si].fields.splice(fi, 1); return n; });
    setSelectedUid(null);
  }, []);
  const updateStep = useCallback((si, updated) => {
    setSteps((prev) => { const n = [...prev]; n[si] = updated; return n; });
  }, []);
  const addStep = useCallback(() => setSteps((prev) => [...prev, emptyStep(prev.length + 1)]), []);
  const handleDeleteStepRequest = useCallback((si) => setDeleteStepDialog({ open: true, stepIdx: si }), []);
  const confirmDeleteStep = useCallback(() => {
    const si = deleteStepDialog.stepIdx;
    setDeleteStepDialog({ open: false, stepIdx: null });
    setSteps((prev) => prev.filter((_, i) => i !== si));
    setActiveStep((prev) => Math.max(0, prev === si ? Math.max(0, si - 1) : prev > si ? prev - 1 : prev));
  }, [deleteStepDialog.stepIdx]);

  // ── DnD ───────────────────────────────────────────────────────────────────
  const handleDragStart = useCallback((e) => { setActiveDragId(e.active.id); setActiveDragData(e.active.data.current); }, []);
  const handleDragOver = useCallback((e) => { setOverId(e.over?.id ?? null); }, []);
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveDragId(null); setActiveDragData(null); setOverId(null);
    if (!over) return;
    const activeData = active.data.current;
    const overData = over.data.current;
    if (activeData?.type === 'step' && overData?.type === 'step' && active.id !== over.id) {
      const oldIdx = steps.findIndex((s) => `${STEP_PREFIX}${s._uid}` === active.id);
      const newIdx = steps.findIndex((s) => `${STEP_PREFIX}${s._uid}` === over.id);
      if (oldIdx >= 0 && newIdx >= 0) {
        setSteps((prev) => arrayMove(prev, oldIdx, newIdx));
        setActiveStep((prev) => {
          if (prev === oldIdx) return newIdx;
          if (oldIdx < prev && newIdx >= prev) return prev - 1;
          if (oldIdx > prev && newIdx <= prev) return prev + 1;
          return prev;
        });
      }
      return;
    }
    const si = activeStep;
    if (activeData?.type === 'palette') {
      const ft = typeMeta(activeData.fieldType);
      const allLabels = steps.flatMap((s) => s.fields).map((f) => f.label);
      const label = uniqueLabel(ft.label, allLabels);
      const key = uniqueKey(label, getAllKeys(steps));
      const newField = { ...emptyField(activeData.fieldType, label), key };
      setSteps((prev) => {
        const next = prev.map((s) => ({ ...s, fields: [...s.fields] }));
        const fields = next[si].fields;
        if (overData?.type === 'dropzone') fields.push(newField);
        else if (overData?.type === 'field') {
          const overIdx = fields.findIndex((f) => f._uid === over.id);
          fields.splice(overIdx >= 0 ? overIdx : fields.length, 0, newField);
        } else fields.push(newField);
        return next;
      });
      setTimeout(() => setSelectedUid(newField._uid), 0);
      return;
    }
    if (activeData?.type === 'field' && active.id !== over.id) {
      setSteps((prev) => {
        const next = prev.map((s) => ({ ...s, fields: [...s.fields] }));
        const fields = next[si].fields;
        const oldIdx = fields.findIndex((f) => f._uid === active.id);
        const newIdx = fields.findIndex((f) => f._uid === over.id);
        if (oldIdx < 0 || newIdx < 0) return prev;
        next[si].fields = arrayMove(fields, oldIdx, newIdx);
        return next;
      });
    }
  }, [activeStep, steps]);

  const activePaletteType = activeDragData?.type === 'palette' ? activeDragData.fieldType : null;
  const activeFieldObj = activeDragData?.type === 'field' ? steps.flatMap((s) => s.fields).find((f) => f._uid === activeDragId) || null : null;

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!def.id || !def.label || !def.icon) {
      setSnack({ open: true, msg: 'Task ID, name and icon are required', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      const cleanField = ({ _uid, ...f }) => ({ ...f, options: f.options || [] });
      const cleanStep = ({ _uid, ...s }) => ({ ...s, fields: s.fields.map(cleanField) });
      const payload = { ...def, steps: steps.map(cleanStep), fields: [] };
      delete payload._id;
      const url = isEdit ? `${API_BASE}/admin/taskdefinitions/${id}` : `${API_BASE}/admin/taskdefinitions`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error(await res.text());
      setSnack({ open: true, msg: isEdit ? 'Updated!' : 'Created!', severity: 'success' });
      setTimeout(() => navigate('/taskforms'), 1200);
    } catch (e) {
      setSnack({ open: true, msg: `Save failed: ${e.message}`, severity: 'error' });
    }
    setSaving(false);
  };

  if (loading) return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <CircularProgress sx={{ color: FIXED_ACCENT }} />
    </Box>
  );

  const deletingStep = deleteStepDialog.stepIdx !== null ? steps[deleteStepDialog.stepIdx] : null;
  const fieldCount = deletingStep?.fields?.length || 0;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
      <EditorCtx.Provider value={{ color: def.color || '#91ce0f' }}>
        <Box sx={{ height: 'calc(100vh - 128px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Top bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexShrink: 0 }}>
            <IconButton size="small" onClick={() => navigate('/taskforms')}
              sx={{ color: '#4a6a85', background: '#f0f8f4', border: '1px solid rgba(16,117,186,0.15)', borderRadius: '10px' }}>
              <ArrowBack fontSize="small" />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f2137', flex: 1 }}>
              {isEdit ? 'Edit Task Definition' : 'New Task Definition'}
            </Typography>
            <FormControlLabel
              control={
                <Switch checked={!!def.active} onChange={(e) => setDef((d) => ({ ...d, active: e.target.checked }))}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: FIXED_ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: FIXED_ACCENT } }} />
              }
              label={<Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#4a6a85' }}>Active</Typography>}
              sx={{ mr: 0 }}
            />
            <Button variant="contained" onClick={handleSave} disabled={saving}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <CheckCircle />}
              sx={{ borderRadius: '10px', px: 2.5, background: FIXED_ACCENT, boxShadow: `0 2px 8px ${alpha(FIXED_ACCENT, 0.35)}`, '&:hover': { background: FIXED_ACCENT, filter: 'brightness(0.9)' } }}>
              {saving ? 'Saving…' : isEdit ? 'Update' : 'Publish'}
            </Button>
          </Box>

          {/* 3-column body */}
          <Box sx={{ flex: 1, display: 'flex', gap: 2, minHeight: 0 }}>
            {/* Left panel */}
            <Box sx={{ width: 260, flexShrink: 0, background: '#fff', borderRadius: '16px', border: '1px solid rgba(16,117,186,0.1)', boxShadow: '0 2px 8px rgba(15,33,55,0.04)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Tabs value={leftTab} onChange={(_, v) => setLeftTab(v)}
                sx={{ borderBottom: '1px solid rgba(16,117,186,0.08)', minHeight: 42, '& .MuiTab-root': { fontSize: '0.78rem', fontWeight: 700, textTransform: 'none', minHeight: 42, py: 0 }, '& .Mui-selected': { color: FIXED_ACCENT }, '& .MuiTabs-indicator': { background: FIXED_ACCENT, height: 2.5, borderRadius: '2px 2px 0 0' } }}>
                <Tab label="Fields" />
                <Tab label="Task Settings" />
              </Tabs>
              <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', ...THIN_SCROLLBAR }}>
                {leftTab === 0 && (
                  <Box sx={{ p: 1.5 }}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#9aacb8', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1.5 }}>Drag to preview →</Typography>
                    <SortableContext items={FIELD_TYPES.map((ft) => `${PALETTE_PREFIX}${ft.type}`)} strategy={verticalListSortingStrategy}>
                      <Stack spacing={1}>
                        {FIELD_TYPES.map((ft) => <DraggablePaletteCard key={ft.type} ft={ft} />)}
                      </Stack>
                    </SortableContext>
                  </Box>
                )}
                {leftTab === 1 && (
                  <TaskSettingsPanel def={def} update={(p) => setDef((d) => ({ ...d, ...p }))} onOpenConfirmModal={() => setConfirmModalOpen(true)} />
                )}
              </Box>
            </Box>

            {/* Center: phone preview */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'stretch', justifyContent: 'center', minHeight: 0, overflow: 'hidden', py: 3 }}>
              <PhonePreview
                def={{ ...def, onLabelChange: (v) => setDef((d) => ({ ...d, label: v })) }}
                steps={steps} activeStep={activeStep} setActiveStep={setActiveStep}
                addStep={addStep} onDeleteStepRequest={handleDeleteStepRequest} updateStep={updateStep}
                selectedField={selectedUid} setSelectedField={setSelectedUid}
                updateField={updateField} deleteField={deleteField} allKeys={allKeys} overId={overId}
                confirmModalOpen={confirmModalOpen}
                onCloseConfirmModal={(action) => {
                  if (action === 'open') setConfirmModalOpen(true);
                  else setConfirmModalOpen(false);
                }}
                onUpdateDef={(p) => setDef((d) => ({ ...d, ...p }))}
                onFieldDragWidth={setFieldDragWidth}
              />
            </Box>

            {/* Right: field settings panel */}
            <RightPanel
              selectedField={selectedField}
              onClearSelection={() => setSelectedUid(null)}
              onChange={handleSelectedFieldChange}
              allKeys={selectedField ? getAllKeys(steps, selectedField._uid) : []}
            />
          </Box>
        </Box>

        {/* Drag overlay */}
        <DragOverlay dropAnimation={null}>
          {activePaletteType && (
            <Box sx={{ opacity: 0.92, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', borderRadius: '12px', width: 240 }}>
              <PaletteCard ft={typeMeta(activePaletteType)} isDragging={false} />
            </Box>
          )}
          {activeFieldObj && (
            <Box sx={{ opacity: 0.92, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', borderRadius: '10px', background: '#fff', px: 1.75, py: 0.5, width: fieldDragWidth }}>
              <SortableFieldCard field={activeFieldObj} stepIdx={activeStep} isSelected={false} onSelect={() => { }} onChange={() => { }} onDelete={() => { }} allKeys={[]} isDragOverlay themeColor={def.color || '#91ce0f'} />
            </Box>
          )}
        </DragOverlay>

        {/* Delete step dialog */}
        <Dialog open={deleteStepDialog.open} onClose={() => setDeleteStepDialog({ open: false, stepIdx: null })} PaperProps={{ sx: { borderRadius: '16px', p: 0.5 } }}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: '#fff3f3', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Warning sx={{ fontSize: 20, color: '#ef4444' }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#0f2137' }}>Delete "{deletingStep?.label || 'Step'}"?</Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 0 }}>
            <DialogContentText sx={{ fontSize: '0.88rem', color: '#4a6a85' }}>
              {fieldCount > 0
                ? <>This step contains <strong>{fieldCount} field{fieldCount !== 1 ? 's' : ''}</strong>. Deleting it will permanently remove all its fields. This cannot be undone.</>
                : <>Are you sure? This cannot be undone.</>}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button onClick={() => setDeleteStepDialog({ open: false, stepIdx: null })} variant="outlined" sx={{ borderRadius: '10px', borderColor: '#e5e7eb', color: '#4a6a85', textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
            <Button onClick={confirmDeleteStep} variant="contained" sx={{ borderRadius: '10px', background: '#ef4444', textTransform: 'none', fontWeight: 700, boxShadow: 'none', '&:hover': { background: '#dc2626' } }}>Delete Step</Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ borderRadius: '12px' }}>{snack.msg}</Alert>
        </Snackbar>
      </EditorCtx.Provider>
    </DndContext>
  );
}
